<template>
  <v-container fluid class="pa-0">
    <!-- Barra de herramientas compacta -->
    <v-toolbar flat dense color="grey lighten-4" class="mb-3">
      <v-toolbar-title class="text-h6">
        <v-icon left>mdi-chart-box</v-icon>
        Resultados de Auditoría
      </v-toolbar-title>
      <v-spacer></v-spacer>
      
      <!-- Filtros en línea -->
      <v-select
        v-model="selectedBatch"
        :items="batchOptions"
        label="Lote"
        item-text="name"
        item-value="id"
        dense
        hide-details
        clearable
        style="max-width: 200px"
        class="mr-3"
      ></v-select>
      
      <v-select
        v-model="filterType"
        :items="filterOptions"
        label="Filtro"
        dense
        hide-details
        style="max-width: 150px"
        class="mr-3"
      ></v-select>
      
      <v-btn
        color="primary"
        small
        @click="exportResults"
        :disabled="!hasResults"
      >
        <v-icon left small>mdi-download</v-icon>
        Exportar
      </v-btn>
    </v-toolbar>

    <!-- KPIs Compactos -->
    <v-row dense class="mb-3">
      <v-col v-for="kpi in kpis" :key="kpi.key" cols="6" md="3">
        <v-card flat class="pa-3" :color="kpi.color" dark>
          <div class="d-flex align-center">
            <v-icon size="28" class="mr-3">{{ kpi.icon }}</v-icon>
            <div>
              <div class="text-overline">{{ kpi.label }}</div>
              <div class="text-h5 font-weight-bold">{{ kpi.value }}</div>
              <div class="text-caption" v-if="kpi.subtitle">{{ kpi.subtitle }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla Principal Compacta -->
    <v-card>
      <v-card-text class="pa-0">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Buscar producto..."
          single-line
          hide-details
          dense
          filled
          flat
          solo
          class="mb-2"
        ></v-text-field>
        
        <v-data-table
          :headers="resultHeaders"
          :items="filteredResults"
          :search="search"
          :items-per-page="20"
          class="elevation-0"
          dense
          :loading="loading"
          :footer-props="{
            'items-per-page-options': [20, 50, 100, -1]
          }"
        >
          <!-- Columna de Producto con información compacta -->
          <template v-slot:item.product="{ item }">
            <div class="py-1">
              <div class="font-weight-medium">{{ item.productName }}</div>
              <div class="text-caption grey--text">
                {{ item.productId }} | {{ item.productRef }}
                <v-chip 
                  x-small 
                  label 
                  class="ml-2"
                  color="grey lighten-2"
                >
                  {{ item.batchName }}
                </v-chip>
              </div>
            </div>
          </template>

          <!-- Columna de Precios -->
          <template v-slot:item.prices="{ item }">
            <div class="text-right">
              <div class="text-caption">
                <span class="grey--text">C:</span> ${{ formatNumber(item.productCost) }}
              </div>
              <div class="text-caption">
                <span class="grey--text">P:</span> ${{ formatNumber(item.productPrice) }}
              </div>
            </div>
          </template>

          <!-- Columna de Cantidades -->
          <template v-slot:item.quantities="{ item }">
            <div class="text-center">
              <v-chip small label outlined :color="getQuantityColor(item)">
                {{ item.systemQuantity }} → {{ item.countedQuantity }}
              </v-chip>
            </div>
          </template>

          <!-- Columna de Diferencia -->
          <template v-slot:item.difference="{ item }">
            <div class="text-center">
              <v-chip
                small
                :color="getDifferenceColor(item.difference)"
                dark
                label
              >
                {{ item.difference > 0 ? '+' : '' }}{{ item.difference }}
                <span class="ml-1 text-caption">({{ item.differencePercent }}%)</span>
              </v-chip>
            </div>
          </template>

          <!-- Columna de Valor de Diferencia -->
          <template v-slot:item.differenceValue="{ item }">
            <div class="text-right font-weight-medium" :class="getDifferenceClass(item.difference)">
              ${{ formatNumber(Math.abs(item.difference * (item.productCost || 0))) }}
            </div>
          </template>

          <!-- Columna de Escaneado -->
          <template v-slot:item.scanInfo="{ item }">
            <div class="text-caption text-center grey--text">
              <v-icon x-small>mdi-barcode-scan</v-icon>
              {{ item.scanCount || 1 }}x
              <br>
              {{ formatTime(item.lastUpdate) }}
            </div>
          </template>

          <!-- Footer personalizado -->
          <template v-slot:body.append v-if="showTotals">
            <tr class="grey lighten-4 font-weight-bold">
              <td>TOTALES</td>
              <td></td>
              <td class="text-center">{{ totals.systemQuantity }}</td>
              <td class="text-center">{{ totals.countedQuantity }}</td>
              <td class="text-center">
                <v-chip small :color="getDifferenceColor(totals.difference)" dark label>
                  {{ totals.difference > 0 ? '+' : '' }}{{ totals.difference }}
                </v-chip>
              </td>
              <td class="text-right">${{ formatNumber(totals.differenceValue) }}</td>
              <td></td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Panel de Productos No Auditados (Colapsable) -->
    <v-expansion-panels flat class="mt-3" v-if="filterType === 'all' || filterType === 'unaudited'">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div class="d-flex align-center">
            <v-icon left color="warning">mdi-alert</v-icon>
            <span class="font-weight-medium">
              Productos No Auditados
              <v-chip small color="warning" dark class="ml-2">
                {{ unauditedProducts.length }}
              </v-chip>
            </span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :headers="unauditedHeaders"
            :items="unauditedProducts"
            :items-per-page="10"
            dense
            class="elevation-0"
          >
            <template v-slot:item.nombre="{ item }">
              <div class="py-1">
                <div>{{ item.nombre }}</div>
                <div class="text-caption grey--text">
                  {{ item.ean }} | {{ item.refe }}
                </div>
              </div>
            </template>
            
            <template v-slot:item.cantidad="{ item }">
              <div class="text-center font-weight-medium">
                {{ Math.round(item.cantidad) }}
              </div>
            </template>
            
            <template v-slot:item.precio="{ item }">
              <div class="text-right">
                ${{ formatNumber(item.precio) }}
              </div>
            </template>
            
            <template v-slot:item.valorTotal="{ item }">
              <div class="text-right font-weight-medium">
                ${{ formatNumber(Math.round(item.precio * item.cantidad)) }}
              </div>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Mini Dashboard de Resumen -->
    <v-row class="mt-3" dense v-if="hasResults">
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-text>
            <div class="text-subtitle-2 mb-2">Resumen por Estado</div>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr v-for="stat in statusSummary" :key="stat.status">
                    <td>
                      <v-icon small :color="stat.color">{{ stat.icon }}</v-icon>
                      {{ stat.status }}
                    </td>
                    <td class="text-right">{{ stat.count }}</td>
                    <td class="text-right">{{ stat.percentage }}%</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-text>
            <div class="text-subtitle-2 mb-2">Valor de las Diferencias</div>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td>Faltantes:</td>
                    <td class="text-right error--text font-weight-bold">
                      -${{ formatNumber(valueSummary.shortage) }}
                    </td>
                  </tr>
                  <tr>
                    <td>Sobrantes:</td>
                    <td class="text-right warning--text font-weight-bold">
                      +${{ formatNumber(valueSummary.surplus) }}
                    </td>
                  </tr>
                  <tr class="grey lighten-4">
                    <td class="font-weight-bold">Diferencia Neta:</td>
                    <td class="text-right font-weight-bold" :class="valueSummary.net >= 0 ? 'success--text' : 'error--text'">
                      ${{ formatNumber(valueSummary.net) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Historial de cierres dinámicos -->
    <v-row v-if="closures.length > 0" class="mt-2">
      <v-col cols="12">
        <v-expansion-panels v-model="closuresPanel" multiple flat>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-icon left color="info">mdi-clipboard-check</v-icon>
                <span class="font-weight-medium">
                  Cierres previos ({{ closures.length }})
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-data-table
                :headers="closureHeaders"
                :items="closures"
                :items-per-page="10"
                dense
                class="elevation-0"
              >
                <template v-slot:item.runAt="{ item }">
                  {{ formatTime(item.runAt) }}
                </template>
                <template v-slot:item.batchIds="{ item }">
                  <span class="text-caption">
                    {{ (item.batchIds || []).length }} lote(s)
                  </span>
                </template>
                <template v-slot:item.skusContados="{ item }">
                  {{ (item.summary && item.summary.skusContados) || 0 }}
                </template>
                <template v-slot:item.valorFaltante="{ item }">
                  <span class="error--text">
                    ${{ formatNumber((item.summary && item.summary.valorFaltante) || 0) }}
                  </span>
                </template>
                <template v-slot:item.valorSobrante="{ item }">
                  <span class="warning--text">
                    ${{ formatNumber((item.summary && item.summary.valorSobrante) || 0) }}
                  </span>
                </template>
                <template v-slot:item.adjustmentRefs="{ item }">
                  <span class="text-caption">
                    {{ formatClosureRefs(item.adjustmentRefs) }}
                  </span>
                </template>
                <template v-slot:item.partial="{ item }">
                  <v-chip
                    x-small
                    :color="item.partial ? 'warning' : item.discarded ? 'grey' : 'success'"
                    text-color="white"
                  >
                    {{ item.partial ? 'Parcial' : item.discarded ? 'Descartado' : 'OK' }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'AuditResults',
  
  props: {
    batches: {
      type: Array,
      required: true
    },
    inventory: {
      type: Array,
      required: true
    },
    audits: {
      type: Array,
      default: () => []
    },
    auditDB: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      selectedBatch: null,
      filterType: 'all',
      search: '',
      loading: false,
      
      filterOptions: [
        { text: 'Todos', value: 'all' },
        { text: 'Con diferencias', value: 'differences' },
        { text: 'Exactos', value: 'exact' },
        { text: 'No auditados', value: 'unaudited' }
      ],
      
      resultHeaders: [
        { text: 'Producto', value: 'product', width: '35%' },
        { text: 'Precios', value: 'prices', align: 'right', width: '15%' },
        { text: 'Stock', value: 'systemQuantity', align: 'center' },
        { text: 'Contado', value: 'countedQuantity', align: 'center' },
        { text: 'Diferencia', value: 'difference', align: 'center' },
        { text: 'Valor Dif.', value: 'differenceValue', align: 'right' },
        { text: 'Auditoría', value: 'scanInfo', align: 'center', width: '10%' }
      ],
      
      unauditedHeaders: [
        { text: 'Producto', value: 'nombre', width: '40%' },
        { text: 'Categoría', value: 'categoria' },
        { text: 'Stock', value: 'cantidad', align: 'center' },
        { text: 'Precio', value: 'precio', align: 'right' },
        { text: 'Valor Total', value: 'valorTotal', align: 'right' }
      ],

      closures: [],
      closuresPanel: [],
      closureHeaders: [
        { text: 'Cierre', value: 'id' },
        { text: 'Fecha', value: 'runAt' },
        { text: 'Lotes', value: 'batchIds' },
        { text: 'SKUs', value: 'skusContados', align: 'center' },
        { text: 'Faltante', value: 'valorFaltante', align: 'right' },
        { text: 'Sobrante', value: 'valorSobrante', align: 'right' },
        { text: 'Ajustes emitidos', value: 'adjustmentRefs' },
        { text: 'Estado', value: 'partial', align: 'center' }
      ]
    };
  },

  async mounted() {
    if (this.auditDB && typeof this.auditDB.getClosures === 'function') {
      try {
        this.closures = await this.auditDB.getClosures();
      } catch (err) {
        console.error('No se pudieron cargar cierres previos:', err);
      }
    }
  },

  watch: {
    audits() {
      this.refreshClosures();
    }
  },

  computed: {
    batchOptions() {
      return [
        { id: null, name: 'Todos' },
        ...this.batches
      ];
    },

    hasResults() {
      return this.audits.length > 0;
    },

    showTotals() {
      return this.filteredResults.length > 0 && this.filteredResults.length <= 100;
    },

    processedResults() {
      const results = [];
      
      this.audits.forEach(audit => {
        const batch = this.batches.find(b => b.id === audit.batchId);
        const product = this.inventory.find(p => p.ean === audit.productId);
        
        if (batch && product) {
          const difference = audit.countedQuantity - audit.systemQuantity;
          const differencePercent = audit.systemQuantity > 0 
            ? Math.round((difference / audit.systemQuantity) * 100)
            : 100;
          
          results.push({
            ...audit,
            productName: product.nombre,
            productRef: product.refe,
            productCost: audit.productCost || product.costo || 0,
            productPrice: audit.productPrice || product.precio || 0,
            batchName: batch.name,
            difference,
            differencePercent,
            status: this.getStatus(difference)
          });
        }
      });
      
      return results;
    },

    filteredResults() {
      let results = this.processedResults;
      
      if (this.selectedBatch) {
        results = results.filter(r => r.batchId === this.selectedBatch);
      }
      
      switch (this.filterType) {
        case 'differences':
          results = results.filter(r => r.difference !== 0);
          break;
        case 'exact':
          results = results.filter(r => r.difference === 0);
          break;
      }
      
      return results;
    },

    unauditedProducts() {
      const auditedIds = new Set(this.audits.map(a => a.productId));
      return this.inventory.filter(p => !auditedIds.has(p.ean));
    },

    kpis() {
      const totalProducts = this.inventory.length;
      const auditedProducts = new Set(this.audits.map(a => a.productId)).size;
      const withDifferences = this.processedResults.filter(r => r.difference !== 0).length;
      const progress = totalProducts > 0 ? Math.round((auditedProducts / totalProducts) * 100) : 0;
      const accuracy = auditedProducts > 0 ? Math.round(((auditedProducts - withDifferences) / auditedProducts) * 100) : 0;
      
      return [
        {
          key: 'progress',
          label: 'Progreso',
          value: `${progress}%`,
          subtitle: `${auditedProducts}/${totalProducts}`,
          icon: 'mdi-chart-donut',
          color: progress === 100 ? 'success' : 'primary'
        },
        {
          key: 'accuracy',
          label: 'Precisión',
          value: `${accuracy}%`,
          subtitle: `${auditedProducts - withDifferences} exactos`,
          icon: 'mdi-target',
          color: accuracy >= 95 ? 'success' : accuracy >= 80 ? 'warning' : 'error'
        },
        {
          key: 'differences',
          label: 'Diferencias',
          value: withDifferences,
          subtitle: 'productos',
          icon: 'mdi-alert-circle',
          color: withDifferences === 0 ? 'success' : 'warning'
        },
        {
          key: 'pending',
          label: 'Pendientes',
          value: totalProducts - auditedProducts,
          subtitle: 'por auditar',
          icon: 'mdi-clock-outline',
          color: 'grey darken-1'
        }
      ];
    },

    totals() {
      return this.filteredResults.reduce((acc, item) => {
        acc.systemQuantity += item.systemQuantity;
        acc.countedQuantity += item.countedQuantity;
        acc.difference += item.difference;
        acc.differenceValue += Math.abs(item.difference * item.productCost);
        return acc;
      }, {
        systemQuantity: 0,
        countedQuantity: 0,
        difference: 0,
        differenceValue: 0
      });
    },

    statusSummary() {
      const counts = this.filteredResults.reduce((acc, item) => {
        acc[item.status] = (acc[item.status] || 0) + 1;
        return acc;
      }, {});
      
      const total = this.filteredResults.length;
      
      return [
        {
          status: 'Exacto',
          count: counts['Exacto'] || 0,
          percentage: total > 0 ? Math.round(((counts['Exacto'] || 0) / total) * 100) : 0,
          color: 'success',
          icon: 'mdi-check-circle'
        },
        {
          status: 'Sobrante',
          count: counts['Sobrante'] || 0,
          percentage: total > 0 ? Math.round(((counts['Sobrante'] || 0) / total) * 100) : 0,
          color: 'warning',
          icon: 'mdi-arrow-up-circle'
        },
        {
          status: 'Faltante',
          count: counts['Faltante'] || 0,
          percentage: total > 0 ? Math.round(((counts['Faltante'] || 0) / total) * 100) : 0,
          color: 'error',
          icon: 'mdi-arrow-down-circle'
        }
      ];
    },

    valueSummary() {
      const summary = this.filteredResults.reduce((acc, item) => {
        const value = item.difference * item.productCost;
        if (item.difference < 0) {
          acc.shortage += Math.abs(value);
        } else if (item.difference > 0) {
          acc.surplus += value;
        }
        return acc;
      }, { shortage: 0, surplus: 0 });
      
      summary.net = summary.surplus - summary.shortage;
      return summary;
    }
  },

  methods: {
    getStatus(difference) {
      if (difference === 0) return 'Exacto';
      if (difference > 0) return 'Sobrante';
      return 'Faltante';
    },

    getDifferenceColor(difference) {
      if (difference === 0) return 'success';
      if (difference > 0) return 'warning';
      return 'error';
    },

    getDifferenceClass(difference) {
      if (difference === 0) return 'success--text';
      if (difference > 0) return 'warning--text';
      return 'error--text';
    },

    getQuantityColor(item) {
      if (item.difference === 0) return 'success';
      if (Math.abs(item.differencePercent) <= 5) return 'warning';
      return 'error';
    },

    getStatusColor(status) {
      switch (status) {
        case 'Exacto': return 'success';
        case 'Sobrante': return 'warning';
        case 'Faltante': return 'error';
        default: return 'grey';
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case 'Exacto': return 'mdi-check-circle';
        case 'Sobrante': return 'mdi-arrow-up-circle';
        case 'Faltante': return 'mdi-arrow-down-circle';
        default: return 'mdi-help-circle';
      }
    },

    formatNumber(value) {
      return new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(Math.round(value || 0));
    },

    formatTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    exportResults() {
      const data = this.filteredResults.map(item => ({
        'Producto': item.productName,
        'EAN': item.productId,
        'Referencia': item.productRef,
        'Lote': item.batchName,
        'Costo': item.productCost,
        'Precio': item.productPrice,
        'Stock Sistema': item.systemQuantity,
        'Stock Contado': item.countedQuantity,
        'Diferencia': item.difference,
        'Porcentaje': `${item.differencePercent}%`,
        'Valor Diferencia': Math.abs(item.difference * item.productCost),
        'Estado': item.status,
        'Veces Escaneado': item.scanCount || 1,
        'Última Actualización': new Date(item.lastUpdate).toLocaleString('es-ES')
      }));
      
      // Agregar resumen
      if (data.length > 0) {
        data.push({});
        data.push({
          'Producto': '=== RESUMEN ===',
          'EAN': '',
          'Referencia': '',
          'Lote': '',
          'Costo': '',
          'Precio': '',
          'Stock Sistema': this.totals.systemQuantity,
          'Stock Contado': this.totals.countedQuantity,
          'Diferencia': this.totals.difference,
          'Porcentaje': '',
          'Valor Diferencia': this.totals.differenceValue,
          'Estado': '',
          'Veces Escaneado': '',
          'Última Actualización': new Date().toLocaleString('es-ES')
        });
        
        // Agregar estadísticas
        data.push({});
        data.push({ 'Producto': '=== ESTADÍSTICAS ===' });
        data.push({ 'Producto': `Total Productos: ${this.inventory.length}` });
        data.push({ 'Producto': `Productos Auditados: ${new Set(this.audits.map(a => a.productId)).size}` });
        data.push({ 'Producto': `Productos con Diferencias: ${this.processedResults.filter(r => r.difference !== 0).length}` });
        data.push({ 'Producto': `Valor Faltantes: -$${this.formatNumber(this.valueSummary.shortage)}` });
        data.push({ 'Producto': `Valor Sobrantes: +$${this.formatNumber(this.valueSummary.surplus)}` });
        data.push({ 'Producto': `Diferencia Neta: $${this.formatNumber(this.valueSummary.net)}` });
      }
      
      // Convertir a CSV
      const csv = this.convertToCSV(data);
      
      // Descargar
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `auditoria_resultados_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
    },

    convertToCSV(data) {
      if (!data.length) return '';
      
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row => 
          headers.map(header => 
            JSON.stringify(row[header] !== undefined && row[header] !== null ? row[header].toString() : '')
          ).join(',')
        )
      ].join('\r\n');
      
      return csv;
    },

    async refreshClosures() {
      if (this.auditDB && typeof this.auditDB.getClosures === 'function') {
        try {
          this.closures = await this.auditDB.getClosures();
        } catch (err) {
          console.error('No se pudieron recargar cierres:', err);
        }
      }
    },

    formatClosureRefs(refs) {
      if (!Array.isArray(refs) || refs.length === 0) return '—';
      return refs
        .map((r) => `${r.type}: ${r.doc_number || r.status}`)
        .join(' · ');
    }
  }
};
</script>

<style scoped>
/* Hacer la tabla más compacta */
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td {
  padding: 4px 12px !important;
  height: auto !important;
}

.v-data-table >>> .v-data-table__wrapper > table > thead > tr > th {
  padding: 8px 12px !important;
}

/* Estilo para los chips */
.v-chip--label {
  border-radius: 4px !important;
}

/* Mini dashboard */
.v-simple-table >>> table > tbody > tr > td {
  padding: 8px 12px !important;
}
</style>
